import type {
  OutboundAction,
  SyncBootstrapResponse,
  SyncChangesResponse,
  SyncMutationsResponse,
  WebUser,
  WordListResponse,
} from '../types';


const TOKEN_KEY = 'geobu:token';
const USER_KEY = 'geobu:user';

export class ApiClient {
  private token: string | null = null;
  private sseSource: EventSource | null = null;

  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
  }

  getToken(): string | null {
    return this.token;
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem(TOKEN_KEY, token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.disconnectStream();
  }

  getSavedUser(): WebUser | null {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  saveUser(user: WebUser) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async startTelegramOAuth(): Promise<{
    url: string;
    token: string;
    browser_cookie?: string;
    cookie_domain?: string | null;
  }> {
    const res = await fetch('/api/web/auth/telegram/oauth/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to start Telegram OAuth: ${res.statusText}`);
    return res.json();
  }

  async checkTelegramOAuthStatus(token: string): Promise<{
    status: 'pending' | 'approved' | 'rejected';
    token?: string;
    user?: WebUser;
    error?: string;
  }> {
    const res = await fetch(`/api/web/auth/telegram/oauth/status?token=${encodeURIComponent(token)}`);
    if (!res.ok) throw new Error(`OAuth status check failed: ${res.statusText}`);
    const data = await res.json();
    if (data.status === 'approved' && data.token) {
      this.setToken(data.token);
      if (data.user) {
        this.saveUser(data.user);
      }
    }
    return data;
  }

  async startDeepLink(): Promise<{
    token: string;
    bot_username: string;
    deep_link: string;
    web_link: string;
    expires_in: number;
  }> {
    const res = await fetch('/api/web/auth/start-deep-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to start session: ${res.statusText}`);
    return res.json();
  }

  async pollSessionStatus(token: string): Promise<{
    status: 'pending' | 'approved' | 'rejected' | 'consumed';
    token?: string;
    user?: WebUser;
    error?: string;
  }> {
    const res = await fetch(`/api/web/auth/session/status?token=${encodeURIComponent(token)}`);
    if (res.status === 410) return { status: 'consumed' };
    if (!res.ok) throw new Error(`Poll error: ${res.statusText}`);
    return res.json();
  }

  async loginWithTelegramWidget(payload: Record<string, unknown>): Promise<{
    token: string;
    user: WebUser;
  }> {
    const res = await fetch('/api/web/auth/telegram-widget', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Widget auth failed: ${res.statusText}`);
    return res.json();
  }

  async getSessionState(): Promise<{
    user: WebUser;
    user_data: Record<string, unknown>;
    actions: OutboundAction[];
  }> {
    const res = await fetch('/api/web/session/state', {
      headers: this.getHeaders(),
    });
    if (!res.ok) {
      if (res.status === 401) {
        this.clearToken();
      }
      const err: any = new Error(`Failed to get session state: ${res.statusText}`);
      err.status = res.status;
      throw err;
    }
    return res.json();

  }

  async sendEvent(req: {
    event_type: 'button' | 'command' | 'reply' | 'file';
    callback_data?: string;
    command?: string;
    text?: string;
    arguments?: string[];
    file?: Record<string, unknown>;
    caption?: string;
  }): Promise<{ status: string; actions: OutboundAction[] }> {
    const res = await fetch('/api/web/events', {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(req),
    });
    if (!res.ok) {
      if (res.status === 401) {
        this.clearToken();
      }
      throw new Error(`Failed to dispatch event: ${res.statusText}`);
    }
    return res.json();
  }

  connectEventStream(onAction: (action: OutboundAction) => void, onConnect?: () => void) {
    if (!this.token) return;
    this.disconnectStream();

    const url = `/api/web/events/stream?token=${encodeURIComponent(this.token)}`;
    const source = new EventSource(url);
    this.sseSource = source;

    source.addEventListener('connected', () => {
      onConnect?.();
    });

    source.addEventListener('action', (event) => {
      try {
        const action = JSON.parse(event.data);
        onAction(action);
      } catch (err) {
        console.error('Failed to parse SSE action:', err);
      }
    });

    source.onerror = () => {
      // Reconnect handled automatically by EventSource
    };
  }

  disconnectStream() {
    if (this.sseSource) {
      this.sseSource.close();
      this.sseSource = null;
    }
  }

  async getUserPreferences(): Promise<{
    transcription: string;
    translation: string;
    selected_dictionaries: string[];
    auto_play_audio: boolean;
    daily_goal: number;
    available_languages: Array<{ code: string; name: string }>;
    available_transcriptions: Array<{ code: string; name: string }>;
    available_dictionaries: Array<{ id: string; code: string; name: string }>;
  }> {
    const res = await fetch('/api/web/user/preferences', {
      headers: this.getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to get preferences: ${res.statusText}`);
    }
    return res.json();
  }

  async updateUserPreferences(payload: {
    transcription?: string;
    translation?: string;
    selected_dictionaries?: string[];
    auto_play_audio?: boolean;
    daily_goal?: number;
  }): Promise<{ status: string; message: string }> {
    const res = await fetch('/api/web/user/preferences', {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error(`Failed to update preferences: ${res.statusText}`);
    }
    return res.json();
  }

  async getWordList(params: {
    filter?: string;
    pos_id?: number | null;
    page?: number;
    limit?: number;
  }): Promise<WordListResponse> {
    const qs = new URLSearchParams();
    if (params.filter) qs.set('filter', params.filter);
    if (params.pos_id !== undefined && params.pos_id !== null) qs.set('pos_id', String(params.pos_id));
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));

    const res = await fetch(`/api/web/words/list?${qs.toString()}`, {
      headers: this.getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to load words list: ${res.statusText}`);
    }
    return res.json();
  }

  async reactToWord(payload: {
    word_id: number;
    action: string;
    dictionary_id?: number;
  }): Promise<{ status: string; message?: string; action?: string }> {
    const res = await fetch('/api/web/words/react', {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error(`Failed to react to word: ${res.statusText}`);
    }
    return res.json();
  }

  async syncBootstrap(
    params: { limit?: number; offset?: number; high_watermark?: number | null },
    signal?: AbortSignal
  ): Promise<SyncBootstrapResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    if (params.high_watermark !== undefined && params.high_watermark !== null) {
      qs.set('high_watermark', String(params.high_watermark));
    }

    const res = await fetch(`/api/web/sync/bootstrap?${qs.toString()}`, {
      headers: this.getHeaders(),
      signal,
    });
    if (!res.ok) {
      if (res.status === 401) this.clearToken();
      const err: any = new Error(`Sync bootstrap failed: ${res.statusText}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async syncChanges(
    params: {
      after_sync_version: number;
      since_time?: string | null;
      max_word_id?: number;
      limit?: number;
    },
    signal?: AbortSignal
  ): Promise<SyncChangesResponse> {
    const qs = new URLSearchParams();
    qs.set('after_sync_version', String(params.after_sync_version));
    if (params.since_time) qs.set('since_time', params.since_time);
    if (params.max_word_id !== undefined) qs.set('max_word_id', String(params.max_word_id));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));

    const res = await fetch(`/api/web/sync/changes?${qs.toString()}`, {
      headers: this.getHeaders(),
      signal,
    });
    if (!res.ok) {
      if (res.status === 401) this.clearToken();
      const err: any = new Error(`Sync changes failed: ${res.statusText}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async syncMutations(
    mutations: Array<Record<string, any>>,
    signal?: AbortSignal
  ): Promise<SyncMutationsResponse> {
    const res = await fetch('/api/web/sync/mutations', {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ mutations }),
      signal,
    });
    if (!res.ok) {
      if (res.status === 401) this.clearToken();
      const err: any = new Error(`Sync mutations failed: ${res.statusText}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }
}

export const api = new ApiClient();

